import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    title = "title",
    multilineTextFieldValue = "multilineTextFieldValue",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.title]: string;
        [fields.multilineTextFieldValue]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];
    const pages = getDataView(state.data.pages, { parentNodeId: nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: nodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[nodeId] ?? {
            parentNodeId: nodeId,
        },
        viewAll: getPageLinkByPageType(state, "NewsListPage")?.url,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasCategoryContext;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    newsParentWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    categoryArticleLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
    articleImage?: InjectableCss;
    articleTitleWrapper?: InjectableCss;
    multilineTextFieldValue?: InjectableCss;
    titleWrapper?: InjectableCss;
    browseAllWrapper?: InjectableCss;
    browseAllLink?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    browseAllWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 40px;
            margin-bottom: 86px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 38px;
                    `,
                    css`
                        margin-bottom: 38px;
                    `,
                    css`
                        margin-bottom: 60px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    browseAllLink: {
        css: css`
            display: inline-flex;
            justify-content: center;
            align-items: center;
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 24px;
            color: ${COLORS.secondary.base};
            padding: 12px 24px;
            width: 238px;
            height: 48px;
            border: 2px solid ${COLORS.secondary.base};
            border-radius: 24px;

            span {
                color: ${COLORS.secondary.base} !important;
            }
        `,
    },
    titleText: {
        size: 20,
        css: css`
            text-align: center;
        `,
    },
    newsParentWrapper: {
        css: css`
            max-width: 290px;
        `,
    },
    articleImage: {
        css: css`
            width: 100%;
            height: auto;
            aspect-ratio: 1.78/1;
            object-fit: contain;
        `,
    },
    articleTitleWrapper: {
        css: css`
            padding: 20px 30px 30px;
        `,
    },
    newsWrapper: {
        css: css`
            background-color: ${COLORS.background.grayShade};
            height: 100%;
        `,
    },
    categoryArticleLink: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            > span {
                text-transform: uppercase;
            }
            p {
                color: ${getProp("theme.typography.p.color")};
            }
        `,
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            line-height: 20px;
            max-height: 72px;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            p {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 24px;
                color: ${getProp("theme.typography.p.color")};
                text-transform: capitalize;
            }
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    `,
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    wrapper: {
        css: css`
            margin-bottom: 50px;
        `,
    },
    multilineTextFieldValue: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    titleWrapper: {
        css: css`
            text-align: center;
            margin: 0 auto 50px !important;
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && this.props.parentNodeId && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && this.props.parentNodeId && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize, title, multilineTextFieldValue },
            parameter,
            viewAll,
        } = this.props;

        if (!pages) {
            return null;
        }

        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"]).reverse();

        const categoryArticlePages = sortedAndPagedPages
            .filter((page: PageProps) => {
                return (
                    page.fields["categoryName"] &&
                    this.props.category?.name &&
                    page.fields["categoryName"].split(",").includes(this.props.category?.name)
                );
            })
            .slice((page - 1) * pageSize, page * pageSize);

        if (categoryArticlePages?.length === 0) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography variant="h2" {...styles.titleText}>
                        {" "}
                        {title}
                    </Typography>
                    <Typography variant="p" {...styles.multilineTextFieldValue}>
                        {multilineTextFieldValue}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.listWrapper} className="detailList">
                    {categoryArticlePages.map((page: PageProps) => (
                        <StyledWrapper {...styles.newsParentWrapper} key={page.id}>
                            <StyledWrapper {...styles.newsWrapper} key={page.id} className="newsWrapper">
                                {page.fields["image"] && (
                                    <Img
                                        {...styles.articleImage}
                                        src={page.fields["image"]}
                                        altText={page.fields["title"]}
                                    />
                                )}
                                <StyledWrapper {...styles.articleTitleWrapper}>
                                    {page.fields["title"] && (
                                        <StyledWrapper>
                                            <Link href={links[page.nodeId]?.url} {...styles.categoryArticleLink}>
                                                {page.fields["title"]}
                                            </Link>
                                        </StyledWrapper>
                                    )}
                                    {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                        <StyledWrapper {...styles.summaryWrapper}>
                                            {parse(
                                                transformContentHtml(
                                                    page.fields["newsSummary"] ||
                                                        truncate(page.fields["newsContent"], { length: 500 }),
                                                ),
                                                parserOptions,
                                            )}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
                <StyledWrapper {...styles.browseAllWrapper}>
                    <Link href={viewAll} {...styles.browseAllLink}>
                        {translate("Browse All")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(withCategory(NewsListView))),
    definition: {
        group: "News List",
        fieldDefinitions: [
            {
                displayName: "CEN PDP Leaf Category Articles",
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "Translatable",
                min: 1,
                max: 20,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.multilineTextFieldValue,
                displayName: "MultilineText",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
